import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type HomeDatePreset =
  | 'last90Days'
  | 'last30Days'
  | 'lastWeek'
  | 'yesterday'
  | 'today'
  | 'custom';

export type HomeFilterKey = 'orgId' | 'country' | 'app' | 'adUnitType' | 'platform' | 'dp_id' | 'sp_id';

export type HomeViewBy = 'Total' | 'App Name' | 'Country' | 'Ad Unit Type' | 'Platform' | 'DP ID' | 'SP ID';

export interface HomeFiltersState {
  orgId: string[];
  country: string[];
  app: string[];
  adUnitType: string[];
  platform: string[];
  dp_id: string[];
  sp_id: string[];
}

export interface HomePageState {
  datePreset: HomeDatePreset;
  customStartDate: string;
  customEndDate: string;
  filters: HomeFiltersState;
  viewBy: HomeViewBy;
}

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDate = (date: Date, offsetDays: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + offsetDays);
  return nextDate;
};

const buildInitialState = (): HomePageState => {
  const today = new Date();
  const yesterday = shiftDate(today, -1);

  return {
    datePreset: 'lastWeek',
    customStartDate: formatDateInput(shiftDate(today, -7)),
    customEndDate: formatDateInput(yesterday),
    filters: {
      orgId: [],
      country: [],
      app: [],
      adUnitType: [],
      platform: [],
      dp_id: [],
      sp_id: []
    },
    viewBy: 'Country'
  };
};

const initialState = buildInitialState();

const homePageSlice = createSlice({
  name: 'homePage',
  initialState,
  reducers: {
    setDatePreset: (state, action: PayloadAction<HomeDatePreset>) => {
      state.datePreset = action.payload;
    },
    setCustomDateRange: (
      state,
      action: PayloadAction<{
        startDate?: string;
        endDate?: string;
      }>
    ) => {
      state.datePreset = 'custom';
      if (action.payload.startDate !== undefined) {
        state.customStartDate = action.payload.startDate;
      }
      if (action.payload.endDate !== undefined) {
        state.customEndDate = action.payload.endDate;
      }
    },
    setAppliedDateRange: (
      state,
      action: PayloadAction<{
        startDate?: string;
        endDate?: string;
      }>
    ) => {
      if (action.payload.startDate !== undefined) {
        state.customStartDate = action.payload.startDate;
      }
      if (action.payload.endDate !== undefined) {
        state.customEndDate = action.payload.endDate;
      }
    },
    setHomeFilter: (
      state,
      action: PayloadAction<{
        key: HomeFilterKey;
        value: string[];
      }>
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setViewBy: (state, action: PayloadAction<HomeViewBy>) => {
      state.viewBy = action.payload;
    },
    resetHomeFilters: (state) => {
      state.filters = initialState.filters;
      state.datePreset = initialState.datePreset;
      state.customStartDate = initialState.customStartDate;
      state.customEndDate = initialState.customEndDate;
      state.viewBy = initialState.viewBy;
    }
  }
});

export const {
  setDatePreset,
  setCustomDateRange,
  setAppliedDateRange,
  setHomeFilter,
  setViewBy,
  resetHomeFilters
} =
  homePageSlice.actions;

export default homePageSlice.reducer;
